import { StageSelect } from "@/components/admin/stage-select";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STALL_DAYS, type SalaryTone, formatMoney, relativeDays, salaryTone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { type CandidateCard, type CandidateStatus, type StageDef, stageMeta } from "@ow/shared";

/** Which optional card fields are shown — toggled from the applications toolbar (audit K2). Pipeline
 *  position and status always show; salary and employer are the discretionary ones. */
export type CardFields = { salary: boolean; employer: boolean };
export const ALL_CARD_FIELDS: CardFields = { salary: true, employer: true };

/** Advance one application's stage and set its status — admin only. When passed, each row carries its
 *  own inline controls instead of a separate duplicate "manage" list above it (D2). The client view is
 *  read-only, so viewers never receive this and get a static, link-through row instead. */
export type MoveFn = (linkId: string, patch: { stage?: string; status?: CandidateStatus }) => void;

// One column system shared by the header row and BOTH row variants (read-only link + admin editable)
// so the columns line up straight down whichever mode is rendered, instead of each variant inventing
// its own proportions (audit design-critical). Identity flexes; the rest are fixed so pills and the
// inline selects occupy the exact same cells. Salary drops on narrow screens; the header matches.
const ROW = "flex items-center gap-4 px-5";
const COL_NAME = "flex min-w-0 flex-1 items-center gap-3";
// Current role is split into its own two columns — the title and the company each get a cell, so
// neither is squeezed into a shared "Sales Lead · Apex" string that truncates the employer away.
// Company drops first on narrow screens (one breakpoint later than the title).
const COL_ROLE = "hidden w-44 shrink-0 md:block";
const COL_COMPANY = "hidden w-36 shrink-0 lg:block";
const COL_SALARY = "hidden w-44 shrink-0 items-center gap-2.5 md:flex";
const COL_STAGE = "w-40 shrink-0";
const COL_TIME = "hidden w-24 shrink-0 sm:block";
const COL_STATUS = "w-28 shrink-0";
// The editable (admin) selects need a touch more room than the read-only readouts so the widest option
// ("Reference Check", "Withdrawn") sits with padding, never clipped — the trigger fills the cell (w-full).
const COL_STAGE_EDIT = "w-44 shrink-0";
const COL_STATUS_EDIT = "w-32 shrink-0";

const STATUS_ORDER: CandidateStatus[] = ["active", "on_hold", "withdrawn", "rejected", "placed"];
const STATUS_LABEL: Record<CandidateStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
};

const TONE_TEXT: Record<NonNullable<SalaryTone>, string> = {
  success: "text-[var(--color-success)]",
  warning: "text-[var(--color-warning)]",
  destructive: "text-[var(--color-destructive)]",
};
const TONE_BG: Record<NonNullable<SalaryTone>, string> = {
  success: "bg-[var(--color-success)]",
  warning: "bg-[var(--color-warning)]",
  destructive: "bg-[var(--color-destructive)]",
};

/** Time-in-stage momentum readout — the signal a client scans for ("is this person moving?"). Compact
 *  ("12d") so a column of them lines up cleanly; only meaningful for still-active candidates. It turns
 *  amber once one has sat past the stall threshold — colour alone carries that (no extra dot doubling up
 *  on the same signal). Full phrasing lives in the tooltip. */
function TimeInStage({ c }: { c: CandidateCard }) {
  if (c.status !== "active") return <span className="text-xs text-muted-foreground/50">—</span>;
  const stalled = c.daysInStage > STALL_DAYS;
  return (
    <span
      className={cn("text-xs tabular-nums", stalled ? "text-[var(--color-warning)]" : "text-muted-foreground")}
      title={`${relativeDays(c.daysInStage)} in this stage${stalled ? " — stalled" : ""}`}
    >
      {c.daysInStage}d
    </span>
  );
}

/** Where the candidate sits in the ladder, on one line: stage name + a muted depth ("3/8"). Sentence
 *  case (not shouty mono-caps) so the table reads calmly and every row aligns on one baseline. */
function StageCell({ label, index, total }: { label: string; index: number; total: number }) {
  return (
    <div className={cn(COL_STAGE, "flex items-baseline gap-1.5")}>
      <span className="truncate text-sm text-foreground">{label}</span>
      {index >= 0 && total > 0 ? (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {index + 1}/{total}
        </span>
      ) : null}
    </div>
  );
}

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

function Row({
  c,
  ladder,
  searchId,
  budgetMin,
  budgetMax,
  budgetCurrency,
  fields,
  onMove,
  onOpenCandidate,
}: {
  c: CandidateCard;
  ladder: StageDef[];
  searchId: string;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetCurrency: string;
  fields: CardFields;
  onMove?: MoveFn;
  onOpenCandidate?: (candidateId: string) => void;
}) {
  const tone = salaryTone(c.salaryExpectation, budgetMin, budgetMax);
  const exp = c.salaryExpectation;
  const hasBand = exp != null && budgetMin != null && budgetMax != null && budgetMax > budgetMin;
  const pct = hasBand ? Math.max(3, Math.min(100, ((exp - budgetMin) / (budgetMax - budgetMin)) * 100)) : 0;
  const pos = stageMeta(ladder, c.stage);
  const actionable = !!onMove;

  const salary = fields.salary ? (
    <div className={COL_SALARY}>
      <span className={cn("shrink-0 text-sm tabular-nums", tone ? TONE_TEXT[tone] : "")}>
        {formatMoney(exp, c.currency)}
      </span>
      {hasBand ? (
        <div
          className="relative h-1.5 min-w-0 flex-1 rounded-full bg-foreground/10"
          title={`Budget ${formatMoney(budgetMin, budgetCurrency)}–${formatMoney(budgetMax, budgetCurrency)}`}
        >
          <span
            className={cn(
              "absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[var(--color-card)]",
              tone ? TONE_BG[tone] : "bg-foreground",
            )}
            style={{ left: `${pct}%` }}
            title={`Expectation ${formatMoney(exp, c.currency)} vs budget ${formatMoney(budgetMin, budgetCurrency)}–${formatMoney(budgetMax, budgetCurrency)}`}
          />
        </div>
      ) : null}
    </div>
  ) : null;

  // Read-only (the client view, or any no-move context): the whole row opens the candidate — as the
  // right-side DRAWER when the caller wires `onOpenCandidate` (a button, since it's an in-page panel),
  // else as a link to the full profile page. Same cells either way. Name, current role and company are
  // each their OWN column — one value per cell — so every row is a single clean line and the employer
  // never gets truncated out of a shared string. Company hides first on narrow screens, then the title.
  if (!actionable) {
    const cls = cn(
      ROW,
      "group w-full py-3 text-left outline-none transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
    );
    const cells = (
      <>
        <div className={COL_NAME}>
          <div className="min-w-0">
            <span className="block truncate text-sm font-medium leading-tight group-hover:underline">{c.fullName}</span>
          </div>
        </div>
        <div className={COL_ROLE}>
          <span className="block truncate text-sm text-muted-foreground">{c.currentTitle ?? "—"}</span>
        </div>
        {fields.employer ? (
          <div className={COL_COMPANY}>
            <span className="block truncate text-sm text-muted-foreground">{c.currentEmployer ?? "—"}</span>
          </div>
        ) : null}
        {salary}
        <StageCell label={pos.label} index={pos.index} total={pos.total} />
        <div className={COL_TIME}>
          <TimeInStage c={c} />
        </div>
        <div className={COL_STATUS}>
          <StatusPill value={c.status} />
        </div>
      </>
    );
    if (onOpenCandidate) {
      return (
        <button type="button" onClick={() => onOpenCandidate(c.candidateId)} className={cls}>
          {cells}
        </button>
      );
    }
    return <div className={cls}>{cells}</div>;
  }

  // Editable (admin only): the row is a container (not a link, so the controls work); the name opens the
  // candidate DRAWER — the one canonical candidate surface (the full page is retired). Name, current role
  // and company are each their OWN column — one clean line, same grid as the read-only view — so the
  // middle of the row carries real data instead of sitting empty while both ends crowd. Stage and status
  // are inline selects that fill their own cells (w-full), never overflowing into each other.
  return (
    <div className={cn(ROW, "group py-2.5 transition-colors hover:bg-accent/40")}>
      <button
        type="button"
        onClick={() => onOpenCandidate?.(c.candidateId)}
        className={cn(
          COL_NAME,
          "rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        )}
      >
        <div className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
          {initials(c.fullName)}
        </div>
        <span className="min-w-0 truncate text-sm font-medium leading-tight group-hover:underline">{c.fullName}</span>
      </button>
      <div className={COL_ROLE}>
        <span className="block truncate text-sm text-muted-foreground">{c.currentTitle ?? "—"}</span>
      </div>
      {fields.employer ? (
        <div className={COL_COMPANY}>
          <span className="block truncate text-sm text-muted-foreground">{c.currentEmployer ?? "—"}</span>
        </div>
      ) : null}
      {salary}
      <div className={COL_STAGE_EDIT}>
        <StageSelect
          className="h-9 w-full"
          value={c.stage}
          stages={ladder}
          onChange={(stage) => {
            if (stage !== c.stage) onMove(c.linkId, { stage });
          }}
        />
      </div>
      <div className={COL_STATUS_EDIT}>
        <Select
          value={c.status}
          onValueChange={(v) => {
            if (v !== c.status) onMove(c.linkId, { status: v as CandidateStatus });
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/** Applications within a role, as a comparison list. We keep only what a client scans straight
 *  down to compare people: pipeline position (N/8), salary expectation vs the budget band, and
 *  status. Per-person detail (days in stage, notice, assessment) lives on the application page.
 *  When `onMove` is passed (admin only) the rows become editable in place — inline stage and status
 *  selects — so there's no separate duplicate "manage" list (D2). The client view is read-only and
 *  passes no `onMove`, so its rows link straight through to each profile. A shared column header sits
 *  above both variants so the list always reads as a labelled table, not a bare stack of names.
 *  Render order is the caller's (the role page sorts most-advanced first). */
export function CandidateList({
  candidates,
  ladder,
  searchId,
  budgetMin,
  budgetMax,
  budgetCurrency,
  fields = ALL_CARD_FIELDS,
  onMove,
  onOpenCandidate,
}: {
  candidates: CandidateCard[];
  ladder: StageDef[];
  searchId: string;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetCurrency: string;
  fields?: CardFields;
  onMove?: MoveFn;
  onOpenCandidate?: (candidateId: string) => void;
}) {
  // Read-only (client) rows carry a momentum readout in place of the admin's inline controls, so the
  // header only advertises the "In stage" column when that's the variant being rendered.
  const readOnly = !onMove;
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className={cn(ROW, "border-b bg-muted/30 py-2.5")}>
        <span className="eyebrow flex-1">Candidate</span>
        <span className="eyebrow hidden w-44 shrink-0 md:block">Current role</span>
        {fields.employer ? <span className="eyebrow hidden w-36 shrink-0 lg:block">Company</span> : null}
        {fields.salary ? <span className="eyebrow hidden w-44 shrink-0 md:block">Salary</span> : null}
        <span className={cn("eyebrow shrink-0", readOnly ? "w-40" : "w-44")}>Stage</span>
        {readOnly ? <span className="eyebrow hidden w-24 shrink-0 sm:block">In stage</span> : null}
        <span className={cn("eyebrow shrink-0", readOnly ? "w-28" : "w-32")}>Status</span>
      </div>
      <div className="divide-y">
        {candidates.map((c) => (
          <Row
            key={c.linkId}
            c={c}
            ladder={ladder}
            searchId={searchId}
            budgetMin={budgetMin}
            budgetMax={budgetMax}
            budgetCurrency={budgetCurrency}
            fields={fields}
            onMove={onMove}
            onOpenCandidate={onOpenCandidate}
          />
        ))}
      </div>
    </div>
  );
}
